import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Schedule from "@/pages/admin/Schedule";
import { Inbox, Briefcase, CalendarCheck, CreditCard, AlertTriangle, CheckCircle2, Sun, Moon } from "lucide-react";

const KPIs = [
  { key: "total_leads", label: "Total Leads", icon: Inbox, color: "bg-blue-50 text-blue-700" },
  { key: "new_leads", label: "New Leads", icon: Inbox, color: "bg-violet-50 text-violet-700" },
  { key: "confirmed_jobs", label: "Confirmed Jobs", icon: Briefcase, color: "bg-indigo-50 text-indigo-700" },
  { key: "jobs_today", label: "Jobs Today", icon: Sun, color: "bg-amber-50 text-amber-800" },
  { key: "jobs_tomorrow", label: "Jobs Tomorrow", icon: Moon, color: "bg-sky-50 text-sky-700" },
  { key: "payment_pending", label: "Payment Pending", icon: CreditCard, color: "bg-rose-50 text-rose-700" },
  { key: "completed_jobs", label: "Completed", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700" },
  { key: "complaints", label: "Complaints", icon: AlertTriangle, color: "bg-red-50 text-red-700" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Overview</h1>
        <p className="text-sm text-slate-500 mt-1">Today&apos;s snapshot of your operations.</p>
      </div>

      <Tabs defaultValue="overview" className="mt-5" data-testid="overview-tabs">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="overview-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="overview-tab-schedule">Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {KPIs.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.key} className="bg-white rounded-xl p-5 ring-1 ring-slate-200" data-testid={`kpi-${k.key}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="mt-4 text-3xl font-heading font-bold text-[#0F172A]">{data.kpis[k.key]}</div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mt-1 font-semibold">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl ring-1 ring-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg">Recent leads</h2>
            <Link to="/admin/leads" className="text-xs text-[#1B3A6B] font-semibold hover:text-[#5BA83F]">View all →</Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {data.recent_leads.map((l) => (
              <li key={l.id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-800">{l.customer_name}</div>
                  <div className="text-xs text-slate-500">{l.service_type} · {l.city}</div>
                </div>
                <StatusBadge status={l.status} />
              </li>
            ))}
            {data.recent_leads.length === 0 && <li className="text-sm text-slate-500 py-2">No leads yet.</li>}
          </ul>
        </div>

        <div className="bg-white rounded-xl ring-1 ring-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg">Upcoming jobs</h2>
            <Link to="/admin/schedule" className="text-xs text-[#1B3A6B] font-semibold hover:text-[#5BA83F]">Schedule →</Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {data.upcoming_jobs.map((j) => (
              <li key={j.id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-800">{j.customer_name}</div>
                  <div className="text-xs text-slate-500">{j.service_type} · {j.date} {j.start_time}</div>
                </div>
                <StatusBadge status={j.status} />
              </li>
            ))}
            {data.upcoming_jobs.length === 0 && <li className="text-sm text-slate-500 py-2">No upcoming jobs.</li>}
          </ul>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="schedule" className="mt-5" data-testid="overview-schedule-tab-content">
          <Schedule />
        </TabsContent>
      </Tabs>
    </div>
  );
}
